import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

// Internal
import Container from "@/components/Container";
import EasTable from "./__components/EasTable";
import { IAttendance } from "@/types/attendance-sheet.types";
import { useUrlParams } from "@/hooks/useUrlParams";
import {
  useGetAttendanceList,
  useGetFamilyActiveCertificates,
} from "@/api/attendance";
import { useGetPaymentsList } from "@/api/payments";
import { useGetProviderDetails } from "@/api/provider";
import GeneralErrorState from "@/components/GeneralErrorState";
import { FamilyCertificate } from "@/api/__types/attendance";
import { IPayment } from "@/types/payment.types";
import { HomeView, getHomeView } from "./__components/home-view";

// Antd
import { Spin } from "antd";

// MISC
import dayjs from "dayjs";

export default function Home() {
  // Search params
  const [searchParams] = useSearchParams();
  const endQueryParam = searchParams.get("end") || "";

  const { familyId, providerId, isProviderView, endDate, startDate, userType } =
    useUrlParams({
      searchParams,
    });

  const homeView = getHomeView(searchParams, userType);
  const isPaymentsView = homeView === HomeView.PAYMENTS;

  // Fetch/pull from cache... the attendance list
  const {
    data: attendanceSheets,
    error: attendanceSheetsError,
    isError: attendanceSheetsErrored,
    isLoading: attendanceSheetsLoading,
  } = useGetAttendanceList({
    familyId,
    providerId,
    startDate: endQueryParam
      ? dayjs(endQueryParam).startOf("month").format("YYYY-MM-DD")
      : startDate,
    endDate: endQueryParam
      ? dayjs(endQueryParam).endOf("month").format("YYYY-MM-DD")
      : endDate,
  });

  const { data: familyCertificates, isLoading: familyCertificatesLoading } =
    useGetFamilyActiveCertificates({
      familyId,
      platform: attendanceSheets?.find((_sheet) => _sheet?.platform)?.platform,
      startDate: endQueryParam
        ? dayjs(endQueryParam).startOf("month").format("YYYY-MM-DD")
        : endDate,
    });

  const {
    data: payments,
    error: paymentsError,
    isError: paymentsErrored,
    isLoading: paymentsLoading,
  } = useGetPaymentsList({
    familyId,
    providerId,
    startDate: endQueryParam
      ? dayjs(endQueryParam).startOf("month").format("YYYY-MM-DD")
      : startDate,
    endDate: endQueryParam
      ? dayjs(endQueryParam).endOf("month").format("YYYY-MM-DD")
      : endDate,
    enabled: isPaymentsView,
  });

  // Fetch/pull from cache... the provider info
  const { isLoading: providerInfoLoading, data: providerInfo } =
    useGetProviderDetails(providerId);

  // Reporting user ID to New Relic
  const newRelicIntervalSet = useRef(false);
  useEffect(() => {
    let attemptCount = 5;
    if (newRelicIntervalSet.current === false && (providerId || familyId)) {
      newRelicIntervalSet.current = true;

      const interval = setInterval(() => {
        if (attemptCount <= 0) {
          clearInterval(interval);
        }

        // @ts-expect-error - newrelic would not be defined in TS window definition
        if (typeof window !== "undefined" && window?.newrelic) {
          console.log(
            "report",
            providerId ? `provider-${providerId}` : `family-${familyId}`,
          );

          // @ts-expect-error - newrelic would not be defined in TS window definition
          window?.newrelic?.setUserId?.(
            providerId ? `provider-${providerId}` : `family-${familyId}`,
          );

          clearInterval(interval);
        }

        attemptCount--;
      }, 2000);
    }
  }, [familyId, newRelicIntervalSet, providerId]);

  if (isPaymentsView && paymentsErrored) {
    return <GeneralErrorState error={paymentsError} />;
  }

  if (!isPaymentsView && attendanceSheetsErrored) {
    return <GeneralErrorState error={attendanceSheetsError} />;
  }

  return (
    <>
      {isProviderView && (
        <Spin spinning={providerInfoLoading}>
          <div className="bg-primary bg-opacity-5 text-center py-12 border-b border-[rgba(0,0,0,0.05)] border-solid">
            <h3 className="text-3xl font-semibold tracking-normal">
              {providerInfo?.name}
            </h3>

            <p className="text-sm inline-flex mx-auto gap-3 items-center mt-1">
              <span>
                <b className="font-semibold">Provider ID:</b> {providerInfo?.id}
              </span>
            </p>
          </div>
        </Spin>
      )}

      <Spin
        spinning={
          isPaymentsView
            ? paymentsLoading
            : attendanceSheetsLoading || familyCertificatesLoading
        }
      >
        <Container>
          <EasTable
            loading={
              isPaymentsView
                ? paymentsLoading
                : attendanceSheetsLoading || familyCertificatesLoading
            }
            attendanceSheets={attendanceSheets as IAttendance[]}
            familyCertificates={familyCertificates as FamilyCertificate[]}
            payments={(payments as IPayment[]) || []}
          />
        </Container>
      </Spin>
    </>
  );
}
